/**
 * Generated-ads slice.
 *
 * Owns the generatedAds collection (merge/remove/update), the companion
 * session controls handle, asset selection + favorites, and the cached ad
 * platform connection config.
 */

import type { StoreApi } from "zustand";
import type { AdsWorkspaceState } from "../useAdsWorkspaceStore.types";
import { getResultId, mergeGeneratedAds } from "../adsDedup";

type SetState = StoreApi<AdsWorkspaceState>["setState"];
type GetState = StoreApi<AdsWorkspaceState>["getState"];

export type GeneratedAdsActions = Pick<
  AdsWorkspaceState,
  | "mergeAds"
  | "removeGeneratedAd"
  | "updateAd"
  | "setCompanionSessionControls"
  | "toggleSelectedAsset"
  | "clearSelectedAssets"
  | "toggleFavoriteAsset"
  | "setAdPlatformConfig"
>;

export function createGeneratedAdsSlice(
  set: SetState,
  _get: GetState,
): GeneratedAdsActions {
  return {
    mergeAds: (ads) =>
      set((state) => ({
        generatedAds: mergeGeneratedAds(state.generatedAds, ads),
      })),

    removeGeneratedAd: (jobId) =>
      set((state) => ({
        generatedAds: state.generatedAds.filter((ad) => getResultId(ad) !== jobId),
        selectedAssetIds: state.selectedAssetIds.filter((id) => id !== jobId),
        favoriteAssetIds: state.favoriteAssetIds.filter((id) => id !== jobId),
      })),

    updateAd: (jobId, updater) =>
      set((state) => ({
        generatedAds: state.generatedAds.map((ad) => (getResultId(ad) === jobId ? updater(ad) : ad)),
      })),

    setCompanionSessionControls: (controls) =>
      set({
        companionSessionControls: controls,
      }),

    toggleSelectedAsset: (assetId) =>
      set((state) => {
        if (!assetId) return state;
        const hasId = state.selectedAssetIds.includes(assetId);
        return {
          selectedAssetIds: hasId
            ? state.selectedAssetIds.filter((id) => id !== assetId)
            : [...state.selectedAssetIds, assetId],
        };
      }),

    clearSelectedAssets: () => set({ selectedAssetIds: [] }),

    toggleFavoriteAsset: (assetId) =>
      set((state) => {
        if (!assetId) return state;
        const hasId = state.favoriteAssetIds.includes(assetId);
        return {
          favoriteAssetIds: hasId
            ? state.favoriteAssetIds.filter((id) => id !== assetId)
            : [...state.favoriteAssetIds, assetId],
        };
      }),

    setAdPlatformConfig: (config) =>
      set((state) => ({
        adPlatformConfig: {
          google: config.google
            ? { ...state.adPlatformConfig.google, ...config.google }
            : state.adPlatformConfig.google,
          meta: config.meta
            ? { ...state.adPlatformConfig.meta, ...config.meta }
            : state.adPlatformConfig.meta,
        },
      })),
  };
}
